import { useEffect, useRef } from 'react';

import { useStore } from '@libs/common-next';

import { isProductAvailableForAutoship, useProduct, variantSupportsAutoshipPurchase } from '@/entities/product';
import { type FormFields, setValue, usePurchaseFormWatch } from '@/entities/purchase-form';

import { resolveDeliveryMethod } from '../lib';

// handles the selection of the delivery method based on the purchase option and the delivery methods data on the client
export const useSelectDeliveryMethod = () => {
    const { selectedStoreId } = useStore();
    const { product, tailored, isLoading, isError } = useProduct();

    const purchaseOption = usePurchaseFormWatch('purchaseOption');
    const selectedDeliveryMethod = usePurchaseFormWatch('fulfilmentMethod');
    const autoshipFrequency = usePurchaseFormWatch('autoshipFrequency');

    // Track previous purchase option to detect changes
    const prevPurchaseOptionRef = useRef<FormFields['purchaseOption']>(purchaseOption);
    const prevProductSkuRef = useRef<string | undefined>(product?.sku);
    const prevAutoshipFrequencyRef = useRef<FormFields['autoshipFrequency']>(autoshipFrequency);

    useEffect(() => {
        if (isLoading || !product) return;

        let effectivePurchaseOption = purchaseOption ?? 'one-time';

        if (effectivePurchaseOption === 'autoship') {
            const autoshipViable = variantSupportsAutoshipPurchase({
                deliveryMethods: product.deliveryMethods,
                isAutoshipEnabledOnVariant: isProductAvailableForAutoship(product),
            });

            if (!autoshipViable) {
                setValue('purchaseOption', 'one-time');
                effectivePurchaseOption = 'one-time';
            }
        }

        const isPurchaseOptionChanged = prevPurchaseOptionRef.current !== purchaseOption;
        const isProductChanged = prevProductSkuRef.current !== product.sku;
        const isAutoshipFrequencyChanged = prevAutoshipFrequencyRef.current !== autoshipFrequency;

        // If "One Time" frequency is selected, force STH delivery method
        if (autoshipFrequency === 'one_time' && effectivePurchaseOption === 'autoship') {
            if (selectedDeliveryMethod !== 'sth') {
                setValue('fulfilmentMethod', 'sth');
            }
            // Update all refs before early return to prevent stale values
            prevPurchaseOptionRef.current = purchaseOption;
            prevProductSkuRef.current = product.sku;
            prevAutoshipFrequencyRef.current = autoshipFrequency;
            return;
        }

        // Use tailored data to determine BOPIS availability if available
        const isBopisStoreData =
            !isError && selectedStoreId ? tailored?.bopisStoreData?.[selectedStoreId]?.offersBopis : undefined;

        // Create delivery methods with updated BOPIS availability from store data
        const baseBopis = product.deliveryMethods.BOPIS;
        const isBopisOneTimeAvailable = isBopisStoreData ?? baseBopis.availablePurchaseOptions?.oneTime ?? false;
        const isBopisAutoshipAvailable = isBopisStoreData ?? baseBopis.availablePurchaseOptions?.autoship ?? false;

        const deliveryMethods = {
            ...product.deliveryMethods,
            BOPIS: {
                ...baseBopis,
                availablePurchaseOptions: {
                    oneTime: isBopisOneTimeAvailable,
                    autoship: isBopisAutoshipAvailable,
                },
            },
        };

        // Resolve delivery method, passing current method to preserve user selection when possible
        const resolvedDeliveryMethod = resolveDeliveryMethod({
            purchaseOption: effectivePurchaseOption,
            deliveryMethods,
            selectedDeliveryMethod,
        });

        // Only update if:
        // 1. No method is currently selected (initial state)
        // 2. Purchase option changed (one-time <-> autoship)
        // 3. Product variant changed
        // 4. Autoship frequency changed (e.g. from one_time to regular frequency)
        // 5. Resolved method differs from selection (e.g. tailored/store disabled bopis while form still has bopis)
        const isContextChanged = isPurchaseOptionChanged || isProductChanged || isAutoshipFrequencyChanged;
        const isResolvedDifferentFromSelected = resolvedDeliveryMethod !== selectedDeliveryMethod;
        const shouldSetFulfillmentMethod =
            !selectedDeliveryMethod || isContextChanged || isResolvedDifferentFromSelected;

        if (shouldSetFulfillmentMethod) {
            setValue('fulfilmentMethod', resolvedDeliveryMethod);
        }

        // Update refs for next render
        prevPurchaseOptionRef.current = purchaseOption;
        prevProductSkuRef.current = product.sku;
        prevAutoshipFrequencyRef.current = autoshipFrequency;
    }, [
        isLoading,
        product,
        purchaseOption,
        selectedDeliveryMethod,
        selectedStoreId,
        tailored,
        autoshipFrequency,
        setValue,
    ]);
};
